import React, { useState, useRef, useEffect } from 'react';
// @ts-ignore
import deleteIcon from '../../image-assets/icon.delete.svg';
// @ts-ignore
import rearrangeIcon from '../../image-assets/icon.rearrange.svg';
// @ts-ignore
import languagesData from '../../data/languages.json';
import './ModalList.css';

interface ModalListProps {
  items: string[];
  onItemClick?: (item: string) => void;
  onDeleteClick?: (item: string) => void;
  onReorder?: (newOrder: string[]) => void;
  emptyMessage?: string;
  draggable?: boolean;
}

const ModalList: React.FC<ModalListProps> = ({
  items,
  onItemClick,
  onDeleteClick,
  onReorder,
  emptyMessage = 'No items',
  draggable = false
}) => {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOverItem, setDraggedOverItem] = useState<string | null>(null);
  const [draggedOverPosition, setDraggedOverPosition] = useState<'above' | 'below' | null>(null);
  const [currentOrder, setCurrentOrder] = useState<string[]>(items);
  const [reorderingItems, setReorderingItems] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Update current order when items prop changes
  useEffect(() => {
    setCurrentOrder(items);
  }, [items]);

  // Clear reordering animation after a short delay
  useEffect(() => {
    if (reorderingItems.length > 0) {
      const timer = setTimeout(() => {
        setReorderingItems([]);
      }, 200); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [reorderingItems]);

  // Create maps for language codes and names, preserving original case
  const languageData = languagesData.languages.reduce((acc: { [key: string]: { name: string, originalCode: string } }, lang: { code: string, name: string }) => {
    // Store both hyphen and underscore versions to handle both formats
    const normalizedCode = lang.code.toLowerCase();
    acc[normalizedCode] = { name: lang.name, originalCode: lang.code };
    // Also store the alternative format (if it contains a hyphen or underscore)
    if (normalizedCode.includes('-')) {
      acc[normalizedCode.replace(/-/g, '_')] = { name: lang.name, originalCode: lang.code };
    } else if (normalizedCode.includes('_')) {
      acc[normalizedCode.replace(/_/g, '-')] = { name: lang.name, originalCode: lang.code };
    }
    return acc;
  }, {});

  const getLanguageDisplay = (langCode: string) => {
    const langInfo = languageData[langCode.toLowerCase()];
    return langInfo || { name: langCode, originalCode: langCode };
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: string) => {
    if (!draggable || !onReorder) return;
    
    // Set dragged item
    setDraggedItem(item);
    setIsDragging(true);
    
    // Create a transparent 1x1 pixel image to hide the default drag preview
    const emptyImg = new Image();
    emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
    
    // Required for Firefox
    e.dataTransfer.setData('text/plain', item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const reorderListItems = (itemToMove: string, targetItem: string, position: 'above' | 'below') => {
    // Don't reorder if we're dragging onto the same item
    if (itemToMove === targetItem) return currentOrder;
    
    const newOrder = [...currentOrder];
    const currentIndex = newOrder.indexOf(itemToMove);
    const targetIndex = newOrder.indexOf(targetItem);
    
    // Skip if indexes are invalid
    if (currentIndex === -1 || targetIndex === -1) return currentOrder;
    
    // Track which items will be affected by reordering
    const affectedItems = new Set<string>();
    
    // Remove the dragged item
    newOrder.splice(currentIndex, 1);
    
    // Calculate the new position
    let newIndex;
    if (position === 'above') {
      newIndex = targetIndex > currentIndex ? targetIndex - 1 : targetIndex;
    } else {
      newIndex = targetIndex > currentIndex ? targetIndex : targetIndex + 1;
    }
    
    // Make sure the index is within bounds
    if (newIndex < 0) newIndex = 0;
    if (newIndex > newOrder.length) newIndex = newOrder.length;
    
    // Get the range of affected items
    const startIdx = Math.min(currentIndex, newIndex);
    const endIdx = Math.max(currentIndex, newIndex);
    
    // Add all items between the start and end indexes to affectedItems
    for (let i = startIdx; i <= endIdx; i++) {
      if (i < newOrder.length) {
        affectedItems.add(newOrder[i]);
      }
    }
    
    // Insert at the new position
    newOrder.splice(newIndex, 0, itemToMove);
    
    // Update the reordering items to trigger animation
    setReorderingItems(Array.from(affectedItems));
    
    return newOrder;
  };

  const debouncedDragOver = (
    item: string, 
    position: 'above' | 'below',
    draggedItem: string
  ) => {
    // Clear any existing debounce timer
    if (dragDebounceRef.current) {
      clearTimeout(dragDebounceRef.current);
    }
    
    // Set a new timer to delay the reordering
    dragDebounceRef.current = setTimeout(() => {
      // Reorder the list after a short delay
      const newOrder = reorderListItems(draggedItem, item, position);
      setCurrentOrder(newOrder);
      dragDebounceRef.current = null;
    }, 50); // Brief delay for smoother movement
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, item: string) => {
    if (!draggable || !onReorder || !draggedItem || draggedItem === item) return;
    
    // Allow drop operation
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Determine if we're hovering above or below the midpoint
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'above' : 'below';
    
    // Only update if something changed
    if (item !== draggedOverItem || position !== draggedOverPosition) {
      setDraggedOverItem(item);
      setDraggedOverPosition(position);
      
      // Use debounced reordering for smoother movement
      if (draggedItem) {
        debouncedDragOver(item, position, draggedItem);
      }
    }
  };

  const handleDragLeave = () => {
    // Don't reset draggedOverItem here to avoid flickering
    // when moving between child elements
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, item: string) => {
    if (!draggable || !onReorder || !draggedItem) return;
    e.preventDefault();
    
    // Cancel any pending debounced operations
    if (dragDebounceRef.current) {
      clearTimeout(dragDebounceRef.current);
      dragDebounceRef.current = null;
    }
    
    // Use the current order that's already been reordered in real-time
    onReorder(currentOrder);
    
    // Reset drag state
    setDraggedItem(null);
    setDraggedOverItem(null);
    setDraggedOverPosition(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    // Cancel any pending debounced operations
    if (dragDebounceRef.current) {
      clearTimeout(dragDebounceRef.current);
      dragDebounceRef.current = null;
    }
    
    // If we never completed a drop but changed the order, still call onReorder
    if (draggedItem && onReorder && 
        JSON.stringify(currentOrder) !== JSON.stringify(items)) {
      onReorder(currentOrder);
    }
    
    setDraggedItem(null);
    setDraggedOverItem(null);
    setDraggedOverPosition(null);
    setIsDragging(false);
  };
  
  // Handle dropping at the end of the list (into the empty space)
  const handleListDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!draggable || !onReorder || !draggedItem || currentOrder.length === 0) return;
    
    // Prevent default to allow drop
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation();
    
    // If we're not over an item or the draggedOverItem is no longer valid,
    // consider it as dropping to the end
    if (!draggedOverItem || !currentOrder.includes(draggedOverItem)) {
      const lastItem = currentOrder[currentOrder.length - 1];
      if (lastItem !== draggedItem) {
        setDraggedOverItem(lastItem);
        setDraggedOverPosition('below');
        
        // Use debounced reordering for the end of the list too
        if (dragDebounceRef.current) {
          clearTimeout(dragDebounceRef.current);
        }
        
        dragDebounceRef.current = setTimeout(() => {
          // Move to the end of the list
          const newOrder = [...currentOrder];
          const draggedIndex = newOrder.indexOf(draggedItem);
          newOrder.splice(draggedIndex, 1);
          newOrder.push(draggedItem);
          setCurrentOrder(newOrder);
          dragDebounceRef.current = null;
        }, 50);
      }
    }
  };
  
  const handleListDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!draggable || !onReorder || !draggedItem) return;
    if (draggedOverItem && currentOrder.includes(draggedOverItem)) return; // Let the item's drop handler handle this
    
    e.preventDefault();
    e.stopPropagation();
    
    // Cancel any pending debounced operations
    if (dragDebounceRef.current) {
      clearTimeout(dragDebounceRef.current);
      dragDebounceRef.current = null;
    }
    
    // The ordering is already handled in handleListDragOver
    onReorder(currentOrder);
    
    // Reset drag state
    setDraggedItem(null);
    setDraggedOverItem(null);
    setDraggedOverPosition(null);
    setIsDragging(false);
  };
  
  // Preserve the dragging item in the list but apply the dragging class
  const renderItems = currentOrder.map((item) => {
    const langInfo = getLanguageDisplay(item);
    const isDragging = item === draggedItem;
    const isDraggedOver = item === draggedOverItem;
    const isReordering = reorderingItems.includes(item);
    const dragOverClass = isDraggedOver 
      ? (draggedOverPosition === 'above' ? 'drag-over-above' : 'drag-over-below')
      : '';
    
    return (
      <div
        key={item}
        className={`modal-list-item ${isDragging ? 'dragging' : ''} ${dragOverClass} ${isReordering ? 'reordering' : ''}`}
        onClick={() => onItemClick?.(item)}
        draggable={draggable && !!onReorder}
        onDragStart={(e) => handleDragStart(e, item)}
        onDragOver={(e) => handleDragOver(e, item)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, item)}
        onDragEnd={handleDragEnd}
      >
        {draggable && onReorder && (
          <div className="modal-list-drag-handle">
            <img src={rearrangeIcon} alt="Reorder" className="drag-icon" />
          </div>
        )}
        <div className="modal-list-item-content">
          <span className="modal-list-item-name">{langInfo.name}</span>
          <span className="modal-list-item-code">{langInfo.originalCode}</span>
        </div>
        {onDeleteClick && (
          <button 
            className="modal-list-delete-button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(item);
            }}
            aria-label={`Delete ${langInfo.name}`}
          >
            <img src={deleteIcon} alt="Delete" className="delete-icon" />
          </button>
        )}
      </div>
    );
  });
  
  return (
    <div 
      className={`modal-list ${isDragging ? 'is-dragging' : ''}`}
      onDragOver={handleListDragOver}
      onDrop={handleListDrop}
    >
      {currentOrder.length === 0 ? (
        <div className="modal-list-empty">{emptyMessage}</div>
      ) : renderItems}
    </div>
  );
};

export default ModalList; 